/**
 * analyticsIdentity — clears the PostHog identity when a device stops belonging
 * to the user who was signed in.
 *
 * WHY (confirmed gap, 2026-07-30)
 * -------------------------------
 * `posthog.identify(user.id, …)` runs on every successful login (Login.tsx) and
 * there was NO `posthog.reset()` anywhere in the codebase. PostHog keeps the
 * identified distinct_id in local storage, so once a device had been identified
 * it kept attributing every later event to that person — including a completely
 * different user's session after a logout on a shared device. That is not a
 * cosmetic reporting issue: it actively misled the diagnosis of Majid Ali
 * Waryam's account across several sessions during the OAuth-linking
 * investigation, because two people's events shared one identity.
 *
 * `posthog.reset()` drops the stored identity and starts a fresh anonymous
 * distinct_id. The next login re-identifies, so nothing is lost.
 *
 * WHERE THIS MAY *NOT* BE CALLED — this is the important half
 * ----------------------------------------------------------
 * Only at a REAL logout, i.e. the same places `clearNativePushToken()` is
 * wired (§3, native FCM token leak fix), plus account deletion. It must never
 * run on the session-SWAP paths, all of which call
 * `supabase.auth.signOut({ scope: 'local' })` directly and never go through
 * `authService.signOut`:
 *
 *   - finalizeOAuth's linked-account swap  (same human, orphan → CNIC account)
 *   - accountLinkingService.completeSocialLink  (same human, link then restore)
 *   - useAuthState recovery / OTP swap, UserContext init cleanup
 *
 * Resetting on those would fragment one person's session in the middle of the
 * exact flow this telemetry exists to debug — the analytics equivalent of the
 * FCM-token rule already recorded in CLAUDE.md §3.
 *
 * Guarded the same way as `captureError`: PROD + `posthog.__loaded`, and it can
 * never throw into a sign-out path.
 */
import posthog from "posthog-js";

/**
 * identifyAnalyticsUser — attach the signed-in user to their PostHog person.
 *
 * WHY `name` IS SENT (approved by Fahad, 2026-07-31)
 * --------------------------------------------------
 * Session replays were only addressable by raw UUID, which made triaging a
 * reported problem ("Majid's merge failed again") a manual UUID hunt. The
 * person's full name makes a replay recognizable at a glance.
 *
 * WHAT IS DELIBERATELY NOT SENT — do not add these
 * ------------------------------------------------
 * CNIC (national ID), phone, and email. Name is the ONLY identity attribute
 * that goes to PostHog. CNIC in particular is already excluded from replays at
 * the input level (`CNICInput` carries `data-sensitive` + `ph-no-capture`), and
 * `posthog-js` is configured to mask password/tel/email/number inputs. Sending
 * any of them as a person property would defeat that masking entirely.
 *
 * `full_name` is a display name users already show to employers and workers
 * across the platform, which is why it is defensible here where a national ID
 * is not. The Privacy Policy's analytics wording was corrected in the same
 * batch so it no longer claims analytics data is anonymized.
 *
 * CALL SITES: `UserContext.loadUserProfile` (covers every authenticated
 * session — password, biometric, OAuth, and app resume) plus `Login.tsx`'s
 * password path, which already holds the profile and identifies slightly
 * earlier. `identify()` is idempotent, so the overlap is harmless.
 */
export function identifyAnalyticsUser(
  userId: string,
  props: { persona?: string | null; fullName?: string | null },
): void {
  try {
    if (!import.meta.env.PROD || !posthog.__loaded || !userId) return;
    const personProps: Record<string, string> = {};
    if (props.persona) personProps.persona = props.persona;
    // Never send an empty/placeholder name — it would overwrite a good one.
    const name = props.fullName?.trim();
    if (name) personProps.name = name;
    posthog.identify(userId, personProps);
  } catch {
    /* telemetry must never break a sign-in path */
  }
}

export function resetAnalyticsIdentity(): void {
  try {
    if (import.meta.env.PROD && posthog.__loaded) {
      posthog.reset();
    }
  } catch {
    /* telemetry must never break sign-out */
  }
}
